import type { Category } from "@/lib/types";

/**
 * List categories. The apps' own categorizer only makes the six built-in ones, but an item may carry any category: an
 * agent picks its own (CLAUDE.md, "Agents are first-class users"). A category the app doesn't know gets its own
 * section, never hidden and never folded into Other.
 */

/** Built-in categories in store-walk order. */
export const BUILT_IN_CATEGORIES: Category[] = [
  "meat",
  "seafood",
  "produce",
  "fruit",
  "dairy",
  "other",
];

export function isBuiltInCategory(category: string): category is Category {
  return (BUILT_IN_CATEGORIES as string[]).includes(category);
}

/** Section order: built-in ones in store-walk order, then any others alphabetically, with Other always last. */
export function orderCategories(categories: Iterable<string>): string[] {
  const present = new Set(categories);
  const builtIn = BUILT_IN_CATEGORIES.filter((c) => c !== "other" && present.has(c));
  const custom = [...present]
    .filter((c) => !isBuiltInCategory(c))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  return [...builtIn, ...custom, ...(present.has("other") ? ["other"] : [])];
}

/** How a category the app doesn't know is shown: as written, with a capital first letter. */
export function customCategoryLabel(category: string): string {
  const label = category.trim();
  return label.charAt(0).toLocaleUpperCase() + label.slice(1);
}
